/* ==========================================================================
   SPICY HOME - SCROLL ENGINE
   The single authority for page scrolling. Nothing else may own scroll.

   Palo Alto ships its own scrolling: a smoothscroll polyfill, 16 scrollTo
   calls, 6 scrollIntoView calls, and 5 places that lock the page with
   overflow:hidden for drawers, popups and the lightbox. Layering Lenis over
   that caused three separate failures:

     1. Lenis's stylesheet was never loaded, so the document kept
        scroll-behavior:smooth. Every programmatic scroll animated twice --
        once natively, once virtually. That is the jump to the footer.
     2. The theme's native scroll calls moved the real scrollTop underneath
        Lenis mid-animation, desynchronising the two.
     3. The theme's scroll locks never told Lenis to stop, so once a drawer
        had opened and closed the wheel appeared dead.

   Rather than patch a 536 KB vendor bundle, this makes Lenis authoritative
   and adapts the theme to it.

   Rules enforced here:
     - One rAF loop. GSAP's ticker drives Lenis; nothing else schedules.
     - One scroll event, broadcast with cached metrics.
     - Measurement happens on resize, never during a scroll frame.
   ========================================================================== */

(function () {
  'use strict';

  if (window.SpicyScroll) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var COARSE = window.matchMedia('(pointer: coarse)').matches;
  var EDITOR = !!(window.Shopify && window.Shopify.designMode);

  /* Smooth wheel is a desktop-pointer enhancement only.
     Touch keeps native momentum -- nothing emulates it convincingly, and
     replacing it is how sites end up feeling broken on a phone.
     The theme editor keeps native scroll so section navigation works. */
  var SMOOTH = !REDUCED && !COARSE && !EDITOR && typeof window.Lenis === 'function';

  var lenis = null;
  var subscribers = [];
  var locked = false;
  var selfLocking = false;

  var metrics = { scroll: 0, limit: 0, vh: 0, progress: 0, velocity: 0, direction: 0 };

  /* ----------------------------------------------------------------------
     1. Lenis's required CSS.
     ---------------------------------------------------------------------- */
  function injectStyles() {
    if (document.getElementById('spicy-scroll-css')) return;
    var css = [
      'html.lenis, html.lenis body { height: auto; }',
      'html.sh-smooth { scroll-behavior: auto !important; }',
      '.lenis.lenis-smooth { scroll-behavior: auto !important; }',
      '.lenis.lenis-smooth [data-lenis-prevent] { overscroll-behavior: contain; }',
      '.lenis.lenis-stopped { overflow: hidden; }',
      '.lenis.lenis-smooth iframe { pointer-events: none; }'
    ].join('\n');
    var el = document.createElement('style');
    el.id = 'spicy-scroll-css';
    el.textContent = css;
    document.head.appendChild(el);
  }

  /* ----------------------------------------------------------------------
     2. Metrics. Read once per resize, never per frame.

     NOTE: this only READS lenis.limit. It cannot correct a stale one --
     that is what refresh() is for. See the boot() note on late layout.
     ---------------------------------------------------------------------- */
  function measure() {
    metrics.vh = window.innerHeight;
    metrics.limit = lenis
      ? lenis.limit
      : Math.max(0, document.documentElement.scrollHeight - metrics.vh);
    if (!metrics.limit || metrics.limit < 0) metrics.limit = 0;
  }

  function broadcast() {
    metrics.progress = metrics.limit > 0 ? metrics.scroll / metrics.limit : 0;
    for (var i = 0; i < subscribers.length; i++) {
      try { subscribers[i](metrics); } catch (e) { /* one bad subscriber must not stop the rest */ }
    }
  }

  /* ----------------------------------------------------------------------
     3. Scroll locks.
     ---------------------------------------------------------------------- */
  function isLockedNow() {
    var html = document.documentElement;
    if (window.getComputedStyle(document.body).overflowY === 'hidden') return true;

    /* <html> is only evidence of the THEME's lock when Lenis is not the one
       holding it. lenis.stop() adds .lenis-stopped, and this file's own
       injected CSS gives that overflow:hidden -- so reading <html> back
       unconditionally makes Lenis's own stopped state look like a page lock.

       That deadlocks syncLock():
         popup opens  -> body hidden -> lenis.stop() -> html hidden
         popup closes -> body visible, but html is STILL hidden
                      -> isLockedNow() is true, locked is true
                      -> now === locked, so syncLock returns early
                      -> lenis.start() is never called
       The wheel is then dead for the rest of the visit and only a reload
       clears it. That is the "frozen right after the popup" report. */
    if (html.classList.contains('lenis-stopped')) return false;

    return window.getComputedStyle(html).overflowY === 'hidden';
  }

  function syncLock() {
    if (!lenis || selfLocking) return;
    var now = isLockedNow();
    if (now === locked) return;
    locked = now;

    selfLocking = true;
    if (now) { lenis.stop(); } else { lenis.start(); refresh(); }
    // rAF does not run in a background tab; clear on a timer as well.
    var freeSelf = function () { selfLocking = false; };
    window.requestAnimationFrame(freeSelf);
    window.setTimeout(freeSelf, 250);
  }

  function watchLocks() {
    if (!('MutationObserver' in window)) return;
    var scheduled = false;
    var mo = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      /* A latch cleared only by rAF stays latched forever in a background
         tab, which would silently disable lock syncing for good. */
      var run = function () { if (!scheduled) return; scheduled = false; syncLock(); };
      window.requestAnimationFrame(run);
      window.setTimeout(run, 250);
    });
    var opts = { attributes: true, attributeFilter: ['style', 'class'] };
    mo.observe(document.body, opts);
    mo.observe(document.documentElement, opts);
  }

  /* ----------------------------------------------------------------------
     4. Route the theme's native scroll calls through Lenis.
     ---------------------------------------------------------------------- */
  function scrollableAncestor(el) {
    var node = el.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
      var s = window.getComputedStyle(node);
      if (/(auto|scroll)/.test(s.overflowY) && node.scrollHeight > node.clientHeight + 1) return node;
      node = node.parentElement;
    }
    return null;
  }

  function patchNativeScrolling() {
    var nativeScrollTo = window.scrollTo.bind(window);
    var nativeScrollBy = window.scrollBy.bind(window);
    var nativeIntoView = Element.prototype.scrollIntoView;

    function target(a, b) {
      if (typeof a === 'object' && a !== null) return a;
      return { left: a, top: b };
    }

    /* The two-argument form -- scrollTo(x, y) -- is the legacy SYNCHRONOUS
       API. Callers expect the page to have moved by the time it returns.
       Handing it to Lenis as an animated tween broke that contract badly:

       GSAP's ScrollTrigger.refresh() measures every trigger by scrolling the
       page to 0, reading each trigger's position, then restoring the previous
       offset -- all synchronously, through window.scrollTo(x, y). With the
       scroll animated instead of applied, the page had not actually moved
       when the measurements were taken, so every trigger was recorded with an
       offset equal to wherever the visitor happened to be. Observed on the
       cinematic hero: start cached at -953 and later at -1824, exactly the
       scroll position at the time, which made the hero begin partway through
       its journey and finish with hundreds of pixels of scroll left doing
       nothing at all.

       Only the options form with an explicit behavior may animate. Smooth
       anchor scrolling does not go through here -- it calls lenis.scrollTo
       directly via SpicyScroll.scrollTo -- so nothing loses its smoothness. */
    window.scrollTo = window.scroll = function (a, b) {
      if (!lenis || locked) return nativeScrollTo(a, b);
      var legacy = (typeof a !== 'object' || a === null);
      var o = target(a, b);
      if (o.top == null) return nativeScrollTo(a, b);
      var immediate = legacy || o.behavior === 'instant' || o.behavior === 'auto';
      lenis.scrollTo(o.top, { immediate: immediate });
    };

    window.scrollBy = function (a, b) {
      if (!lenis || locked) return nativeScrollBy(a, b);
      var legacy = (typeof a !== 'object' || a === null);
      var o = target(a, b);
      if (o.top == null) return nativeScrollBy(a, b);
      lenis.scrollTo(metrics.scroll + o.top, { immediate: legacy || o.behavior === 'instant' });
    };

    Element.prototype.scrollIntoView = function (arg) {
      // Scrolling inside an overflow container is not our business.
      if (!lenis || locked || scrollableAncestor(this)) {
        return nativeIntoView.apply(this, arguments);
      }
      var opts = (typeof arg === 'object' && arg !== null) ? arg : {};
      lenis.scrollTo(this, {
        immediate: opts.behavior === 'instant' || opts.behavior === 'auto',
        offset: -headerOffset()
      });
    };
  }

  function headerOffset() {
    var header = document.getElementById('shopify-section-header');
    if (!header) return 0;
    var s = window.getComputedStyle(header);
    if (s.position !== 'sticky' && s.position !== 'fixed') return 0;
    return header.offsetHeight || 0;
  }

  /* ----------------------------------------------------------------------
     5. Anchors.
     ---------------------------------------------------------------------- */
  function resolveAnchor(name) {
    if (!name) return null;
    var el = document.getElementById(name);
    if (el) return el;
    var esc = window.CSS && window.CSS.escape ? window.CSS.escape(name) : name;
    return document.querySelector('[id$="__' + esc + '"]') ||
           document.querySelector('[data-sh-anchor="' + esc + '"]');
  }

  function onClick(e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    var a = e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;

    var href = a.getAttribute('href') || '';
    if (href.charAt(0) !== '#' || href === '#') return;

    var el = resolveAnchor(href.slice(1));
    if (!el) return;

    e.preventDefault();
    scrollTo(el);
    if (history.replaceState) history.replaceState(null, '', href);
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }

  /* ----------------------------------------------------------------------
     6. Inner scrollables.
     ---------------------------------------------------------------------- */
  var PREVENT = [
    '[data-cart-drawer]', '.cart-drawer', '[data-nav-drawer]', '.nav-drawer',
    '.drawer__inner', '.popup__content', '.pswp', '[data-lenis-prevent-host]'
  ].join(',');

  function markPrevented(scope) {
    var nodes = (scope || document).querySelectorAll(PREVENT);
    for (var i = 0; i < nodes.length; i++) nodes[i].setAttribute('data-lenis-prevent', '');
  }

  /* ----------------------------------------------------------------------
     7. Public API
     ---------------------------------------------------------------------- */
  function scrollTo(target, opts) {
    opts = opts || {};
    var offset = opts.offset != null ? opts.offset : -headerOffset();
    if (lenis) { lenis.scrollTo(target, { offset: offset, immediate: !!opts.immediate }); return; }

    var top = typeof target === 'number'
      ? target
      : target.getBoundingClientRect().top + window.pageYOffset + offset;
    window.scrollTo({ top: top, behavior: opts.immediate || REDUCED ? 'auto' : 'smooth' });
  }

  function refresh() {
    if (lenis) lenis.resize();
    measure();
    markPrevented(document);
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    broadcast();
  }

  /* ----------------------------------------------------------------------
     8. Boot
     ---------------------------------------------------------------------- */
  function boot() {
    injectStyles();
    document.documentElement.classList.add('sh-smooth');

    if (SMOOTH) {
      lenis = new window.Lenis({
        /* A per-frame lerp, deliberately, rather than duration + easing.

           A duration tween restarts on every wheel event and runs an
           exponential-out curve to completion. The head of that curve is
           fast, but the tail is very long: the last few percent keeps
           crawling for the better part of a second after the finger has
           stopped. On an ordinary page that reads as luxury. On a
           scroll-scrubbed hero it reads as the camera drifting on its own,
           and as a delay between the hand and the picture.

           A lerp has an immediate onset and an exponential settle: the camera
           leaves with the finger and still arrives softly. 0.11 lands at
           roughly 150ms at 60fps, weighted without being laggy. Lenis
           normalises this by delta time, so 120Hz feels identical. */
        lerp: 0.11,
        smoothWheel: true,
        syncTouch: false,
        wheelMultiplier: 1,
        touchMultiplier: 1.6,
        autoResize: true
      });

      lenis.on('scroll', function (e) {
        metrics.scroll = e.animatedScroll != null ? e.animatedScroll : e.scroll;
        metrics.velocity = e.velocity || 0;
        metrics.direction = e.direction || 0;
        metrics.limit = e.limit != null ? e.limit : metrics.limit;
        if (window.ScrollTrigger) window.ScrollTrigger.update();
        broadcast();
      });

      // One loop for the whole site.
      if (window.gsap) {
        window.gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
        window.gsap.ticker.lagSmoothing(0);
      } else {
        var raf = function (t) { lenis.raf(t); window.requestAnimationFrame(raf); };
        window.requestAnimationFrame(raf);
      }

      patchNativeScrolling();
      watchLocks();
    } else {
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          metrics.scroll = window.pageYOffset;
          broadcast();
        });
      }, { passive: true });
    }

    measure();
    markPrevented(document);
    document.addEventListener('click', onClick, false);

    /* ------------------------------------------------------------------
       RE-MEASURE AFTER LATE LAYOUT.

       Lenis caches the scrollable limit when it is constructed, at
       DOMContentLoaded -- before lazy images, the hero video and the webfonts
       have arrived. It then clamps every scroll to that stale number.

       Measured on the live homepage, cold cache: the real bottom was 20823
       but lenis.limit was 17126, so the last 3697px -- the whole footer --
       could not be reached, and the wheel went dead as you approached that
       false bottom. A reload felt fine only because the images were then in
       cache and sized during first layout. That is the "it works after a
       refresh" report.

       measure() cannot fix this: it READS lenis.limit, so it only copies the
       stale number into metrics. Only lenis.resize() recomputes it, which is
       what refresh() does.
       ------------------------------------------------------------------ */
    var refreshTimer = null;
    var refreshing = false;
    var pending = false;

    /* The guard is released on the next frame, but rAF does not run in a
       background tab, so it is released on a timer as well. A page opened in
       an unfocused tab must not be able to hold the guard forever. */
    function releaseGuard() {
      var released = false;
      var free = function () {
        if (released) return;
        released = true;
        refreshing = false;
        if (pending) { pending = false; scheduleRefresh(); }
      };
      window.requestAnimationFrame(free);
      window.setTimeout(free, 250);
    }

    function scheduleRefresh() {
      /* A request arriving mid-refresh is DEFERRED, never dropped.

         Dropping it is how /products/catena-sofa ended up 613px short and
         stayed there: the page grew exactly once, during the guard window,
         and because nothing grew again afterwards there was no second event
         to recover on. Deferring costs one extra pass; dropping costs the
         footer. */
      if (refreshing) { pending = true; return; }
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(function () {
        refreshing = true;
        refresh();
        releaseGuard();
      }, 150);
    }

    /* Apps inject content on their own schedule, after load and without
       always resizing the body in a way the observer catches. Two cheap
       settle passes make the final measurement robust to that. */
    function settle() {
      window.setTimeout(scheduleRefresh, 800);
      window.setTimeout(scheduleRefresh, 2500);
    }

    window.addEventListener('resize', scheduleRefresh, { passive: true });
    window.addEventListener('orientationchange', scheduleRefresh);

    // Everything below the fold has settled by here: images, video, iframes.
    if (document.readyState === 'complete') {
      scheduleRefresh();
      settle();
    } else {
      window.addEventListener('load', function () { scheduleRefresh(); settle(); }, { once: true });
    }

    // A tab opened in the background measures while throttled. Re-measure the
    // moment it is actually looked at.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) scheduleRefresh();
    });

    // Webfonts change every line box, and so the height of the whole page.
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(scheduleRefresh).catch(function () {});
    }

    /* The body keeps growing as lazysizes resolves images well after load.
       Recompute properly each time rather than copying a stale limit. */
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(scheduleRefresh);
      ro.observe(document.body);
    }

    document.addEventListener('shopify:section:load', function (e) {
      markPrevented(e.target);
      scheduleRefresh();
    });

    metrics.scroll = window.pageYOffset;
    broadcast();
  }

  window.SpicyScroll = {
    onScroll: function (fn) { if (typeof fn === 'function') { subscribers.push(fn); fn(metrics); } },
    metrics: function () { return metrics; },
    scrollTo: scrollTo,
    refresh: refresh,
    lenis: function () { return lenis; },
    smooth: function () { return !!lenis; },
    reduced: REDUCED
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
